import type { LyricLine, LyricWord } from "@/types"

export function ensureWords(line: LyricLine): LyricLine {
  if (line.words && line.words.length > 0) return line
  const tokens = (line.text || "")
    .split(/\s+/)
    .map((s) => s.trim())
    .filter(Boolean)
  const words: LyricWord[] = tokens.map((text) => ({ text, time: null }))
  return { ...line, words }
}

export function autoDistributeWords(
  line: LyricLine,
  nextLineStart: number | null,
): LyricLine {
  if (line.time == null) return line
  const ensured = ensureWords(line)
  const words = ensured.words ?? []
  if (words.length === 0) return ensured
  const start = line.time
  const end = nextLineStart ?? start + 2.0
  const span = end - start
  const step = words.length > 0 ? span / words.length : 0
  const next: LyricWord[] = words.map((w, i) => ({
    text: w.text,
    time: start + step * i,
  }))
  return { ...ensured, words: next }
}

export function clearWordTimes(line: LyricLine): LyricLine {
  if (!line.words) return line
  const next: LyricWord[] = line.words.map((w) => ({
    text: w.text,
    time: null,
  }))
  return { ...line, words: next }
}

export function firstWordWithoutTime(line: LyricLine): number {
  const words = line.words ?? []
  for (let i = 0; i < words.length; i++) {
    if (words[i].time == null) return i
  }
  return -1
}

export function nudgeWord(
  line: LyricLine,
  wordIdx: number,
  delta: number,
): LyricLine {
  const ensured = ensureWords(line)
  const words = ensured.words ?? []
  if (wordIdx < 0 || wordIdx >= words.length) return ensured
  const current = words[wordIdx].time
  if (current == null) return ensured
  const next: LyricWord[] = words.map((w, i) =>
    i === wordIdx ? { text: w.text, time: current + delta } : w,
  )
  return { ...ensured, words: next }
}

export function setWordTime(
  line: LyricLine,
  wordIdx: number,
  time: number | null,
): LyricLine {
  const ensured = ensureWords(line)
  const words = ensured.words ?? []
  if (wordIdx < 0 || wordIdx >= words.length) return ensured
  const next: LyricWord[] = words.map((w, i) =>
    i === wordIdx ? { text: w.text, time } : w,
  )
  if (wordIdx === 0) {
    return { ...ensured, time, words: next }
  }
  return { ...ensured, words: next }
}

export function lineTextFromWords(line: LyricLine): string {
  if (!line.words || line.words.length === 0) return line.text
  return line.words.map((w) => w.text).join(" ")
}

export function tokenize(text: string): LyricWord[] {
  return (text || "")
    .split(/\s+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((t) => ({ text: t, time: null }))
}

export function setLineTime(
  line: LyricLine,
  t: number,
  nextStart: number | null,
): LyricLine {
  const stamped: LyricLine = { ...line, time: t }
  return autoDistributeWords(stamped, nextStart)
}

export function withTokenizedWords(line: LyricLine): LyricLine {
  const tokens = tokenize(line.text)
  const prev = line.words ?? []
  const matches =
    tokens.length === prev.length &&
    tokens.every((t, i) => t.text === prev[i]?.text)
  if (matches) return { ...line, words: prev }
  return { ...line, time: tokens[0]?.time ?? null, words: tokens }
}
